import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "../ui/Modal";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { useToast } from "../ui/Toast";
import { useCreateRepo } from "./useRepos.js";
import { repoHome } from "../lib/paths";
import { useTranslation, apiErrorMessage } from "../i18n";

const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly wsSlug: string;
}

export function CreateRepoModal({ open, onClose, wsSlug }: Props) {
  const { t } = useTranslation("repos");
  const { t: tErr } = useTranslation("errors");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const toast = useToast();
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [defaultBranch, setDefaultBranch] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const create = useCreateRepo(wsSlug);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSlug("");
      setDescription("");
      setDefaultBranch("");
      setSlugTouched(false);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!slug.trim() || !KEBAB_RE.test(slug.trim())) return;
    create.mutate(
      {
        slug: slug.trim(),
        description: description.trim() || undefined,
        defaultBranch: defaultBranch.trim() || undefined,
      },
      {
        onSuccess: (repo) => {
          onClose();
          toast.success(t("create.toastCreated", { slug: repo.slug }));
          navigate(repoHome(wsSlug, repo.slug));
        },
      },
    );
  }

  const isConflict = create.error?.code === "conflict";
  const slugValidationError =
    slugTouched && slug && !KEBAB_RE.test(slug)
      ? t("create.slugPattern")
      : isConflict
      ? tErr("repoSlugExists")
      : undefined;
  const genericError =
    create.error && !isConflict ? apiErrorMessage(create.error, tErr) : undefined;

  const canSubmit = slug.trim().length > 0 && KEBAB_RE.test(slug.trim()) && !create.isPending;

  return (
    <Modal open={open} onClose={onClose} title={t("create.title")}>
      {genericError ? (
        <Banner kind="error" className="mb-4">{genericError}</Banner>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field
          label={t("create.slugLabel")}
          error={slugValidationError}
        >
          {({ id, ...ariaProps }) => (
            <>
              <TextInput
                id={id}
                type="text"
                autoComplete="off"
                required
                placeholder={t("create.slugPlaceholder")}
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                hasError={!!slugValidationError}
                {...ariaProps}
              />
              {slug && KEBAB_RE.test(slug) && (
                <p className="mt-1 text-xs text-slate-500">
                  /{wsSlug}/<span className="text-slate-300">{slug}</span>
                </p>
              )}
            </>
          )}
        </Field>

        <Field label={t("create.descriptionLabel")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("create.defaultBranchLabel")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              placeholder={t("create.defaultBranchPlaceholder")}
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
          >
            {create.isPending ? t("create.submitting") : t("create.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
